/**
 * 마크다운 파이프 테이블 파서 유틸리티
 * Phase 3: 표 컨텍스트 메뉴의 핵심 엔진
 */

/**
 * 마크다운 테이블 텍스트를 파싱
 * @param {string} text - 파이프 테이블 텍스트
 * @returns {{ headers: string[], separator: string, rows: string[][], raw: string[] }}
 */
export function parseTable(text) {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length < 2) return null;

  const parseLine = (line) =>
    line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

  const headers = parseLine(lines[0]);

  // Check separator line
  const sepLine = lines[1].trim();
  if (!/^\|?[\s\-:|]+(\|[\s\-:|]+)*\|?$/.test(sepLine)) return null;

  const rows = lines.slice(2).map(parseLine);

  return { headers, separator: lines[1], rows, raw: lines };
}

/**
 * 파싱된 테이블을 마크다운 텍스트로 직렬화
 */
export function serializeTable(table) {
  const { headers, rows } = table;
  const colCount = headers.length;

  // Calculate column widths
  const widths = headers.map((h, i) => {
    const cellWidths = [h.length, ...rows.map(r => (r[i] || '').length)];
    return Math.max(3, ...cellWidths);
  });

  const pad = (str, w) => (str || '').padEnd(w);

  const headerLine = '| ' + headers.map((h, i) => pad(h, widths[i])).join(' | ') + ' |';
  const sepLine = '|' + widths.map(w => '-'.repeat(w + 2)).join('|') + '|';
  const bodyLines = rows.map(
    row => '| ' + Array.from({ length: colCount }, (_, i) => pad(row[i] || '', widths[i])).join(' | ') + ' |'
  );

  return [headerLine, sepLine, ...bodyLines].join('\n');
}

/**
 * CM6 state에서 커서가 테이블 내부인지 판별
 */
export function isInTable(state, pos) {
  const range = getTableRange(state, pos);
  return range !== null;
}

/**
 * 커서 위치에서 테이블의 시작/끝 범위 반환
 */
export function getTableRange(state, pos) {
  if (pos === undefined) pos = state.selection.main.head;
  const doc = state.doc;
  const currentLine = doc.lineAt(pos);

  // Check if current line looks like a table line
  if (!isTableLine(currentLine.text)) return null;

  // Find start of table
  let startLine = currentLine.number;
  while (startLine > 1) {
    const prevLine = doc.line(startLine - 1);
    if (!isTableLine(prevLine.text)) break;
    startLine--;
  }

  // Find end of table
  let endLine = currentLine.number;
  while (endLine < doc.lines) {
    const nextLine = doc.line(endLine + 1);
    if (!isTableLine(nextLine.text)) break;
    endLine++;
  }

  // Must have at least 2 lines (header + separator)
  if (endLine - startLine < 1) return null;

  const from = doc.line(startLine).from;
  const to = doc.line(endLine).to;

  return { from, to, startLine, endLine };
}

/**
 * 현재 커서의 셀 좌표 반환
 */
export function getCellIndex(state, pos) {
  if (pos === undefined) pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const currentLine = doc.lineAt(pos);
  const row = currentLine.number - range.startLine;

  // Find column by counting pipes before cursor
  const lineText = currentLine.text;
  const offsetInLine = pos - currentLine.from;
  let col = -1;
  for (let i = 0; i < offsetInLine; i++) {
    if (lineText[i] === '|') col++;
  }
  if (col < 0) col = 0;

  return { row, col };
}

/**
 * 테이블에 행 추가
 */
export function addRow(state, position) {
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const tableText = doc.sliceString(range.from, range.to);
  const table = parseTable(tableText);
  if (!table) return null;

  const colCount = table.headers.length;
  const emptyRow = Array(colCount).fill('');

  // position: 'above' or 'below'
  const cell = getCellIndex(state, pos);
  if (!cell) return null;

  const bodyRow = cell.row - 2; // subtract header and separator
  if (bodyRow < 0) {
    // Cursor is on header or separator - add at beginning
    table.rows.splice(0, 0, emptyRow);
  } else if (position === 'above') {
    table.rows.splice(bodyRow, 0, emptyRow);
  } else {
    table.rows.splice(bodyRow + 1, 0, emptyRow);
  }

  return { from: range.from, to: range.to, insert: serializeTable(table) };
}

/**
 * 테이블에서 행 삭제
 */
export function deleteRow(state) {
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const tableText = doc.sliceString(range.from, range.to);
  const table = parseTable(tableText);
  if (!table) return null;

  const cell = getCellIndex(state, pos);
  if (!cell) return null;

  const bodyRow = cell.row - 2;
  if (bodyRow < 0) return null; // Can't delete header or separator
  if (table.rows.length <= 1) return null; // Keep at least 1 row

  table.rows.splice(bodyRow, 1);
  return { from: range.from, to: range.to, insert: serializeTable(table) };
}

/**
 * 테이블에 열 추가
 */
export function addColumn(state, position) {
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const tableText = doc.sliceString(range.from, range.to);
  const table = parseTable(tableText);
  if (!table) return null;

  const cell = getCellIndex(state, pos);
  if (!cell) return null;

  const colIndex = position === 'left' ? cell.col : cell.col + 1;
  table.headers.splice(colIndex, 0, `Col ${table.headers.length + 1}`);
  table.rows.forEach(row => row.splice(colIndex, 0, ''));

  return { from: range.from, to: range.to, insert: serializeTable(table) };
}

/**
 * 테이블에서 열 삭제
 */
export function deleteColumn(state) {
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const tableText = doc.sliceString(range.from, range.to);
  const table = parseTable(tableText);
  if (!table) return null;

  const cell = getCellIndex(state, pos);
  if (!cell) return null;

  if (table.headers.length <= 1) return null; // Keep at least 1 column

  table.headers.splice(cell.col, 1);
  table.rows.forEach(row => row.splice(cell.col, 1));

  return { from: range.from, to: range.to, insert: serializeTable(table) };
}

/**
 * 행 복사 (클립보드용 텍스트 반환)
 */
export function copyRow(state) {
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const currentLine = doc.lineAt(pos);
  return currentLine.text;
}

/**
 * 열 복사
 */
export function copyColumn(state) {
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const tableText = doc.sliceString(range.from, range.to);
  const table = parseTable(tableText);
  if (!table) return null;

  const cell = getCellIndex(state, pos);
  if (!cell) return null;

  const values = [table.headers[cell.col], ...table.rows.map(r => r[cell.col] || '')];
  return values.join('\n');
}

/**
 * 번호열 삽입 (첫 번째 열에 1, 2, 3...)
 */
export function addNumberColumn(state) {
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const tableText = doc.sliceString(range.from, range.to);
  const table = parseTable(tableText);
  if (!table) return null;

  table.headers.unshift('#');
  table.rows.forEach((row, i) => row.unshift(String(i + 1)));

  return { from: range.from, to: range.to, insert: serializeTable(table) };
}

/**
 * 테이블 전체 삭제
 */
export function getDeleteTableChange(state) {
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  // Include trailing newline if exists
  const doc = state.doc;
  let to = range.to;
  if (to < doc.length && doc.sliceString(to, to + 1) === '\n') to++;

  return { from: range.from, to };
}

/**
 * 열 잘라내기 (클립보드 텍스트 + 삭제 변경 반환)
 */
export function cutColumn(state) {
  const clipText = copyColumn(state);
  if (!clipText) return null;
  const change = deleteColumn(state);
  if (!change) return null;
  return { clipText, change };
}

/**
 * 클립보드에서 행 붙여넣기 (위에)
 */
export function pasteRowAbove(state, clipboardText) {
  if (!clipboardText) return null;
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const tableText = doc.sliceString(range.from, range.to);
  const table = parseTable(tableText);
  if (!table) return null;

  const cell = getCellIndex(state, pos);
  if (!cell) return null;

  // Parse clipboard as pipe-separated row or tab-separated
  let cells;
  if (clipboardText.includes('|')) {
    cells = clipboardText.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
  } else {
    cells = clipboardText.split('\t').map(c => c.trim());
  }

  // Pad/trim to match column count
  while (cells.length < table.headers.length) cells.push('');
  cells = cells.slice(0, table.headers.length);

  const bodyRow = cell.row - 2;
  const insertAt = bodyRow < 0 ? 0 : bodyRow;
  table.rows.splice(insertAt, 0, cells);

  return { from: range.from, to: range.to, insert: serializeTable(table) };
}

/**
 * 클립보드에서 열 붙여넣기 (왼쪽에)
 */
export function pasteColumnLeft(state, clipboardText) {
  if (!clipboardText) return null;
  const pos = state.selection.main.head;
  const range = getTableRange(state, pos);
  if (!range) return null;

  const doc = state.doc;
  const tableText = doc.sliceString(range.from, range.to);
  const table = parseTable(tableText);
  if (!table) return null;

  const cell = getCellIndex(state, pos);
  if (!cell) return null;

  // Parse clipboard: newline-separated values
  const values = clipboardText.split('\n').map(v => v.trim());
  const header = values[0] || 'Col';
  const rowValues = values.slice(1);

  table.headers.splice(cell.col, 0, header);
  table.rows.forEach((row, i) => row.splice(cell.col, 0, rowValues[i] || ''));

  return { from: range.from, to: range.to, insert: serializeTable(table) };
}

// ─── Helpers ───

function isTableLine(text) {
  const trimmed = text.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.includes('|');
}
